import React, { useCallback, useEffect, useState } from 'react';
import { GraduationCap, RefreshCw, Sparkles } from 'lucide-react';
import { apiService } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import LoadingSpinner from '../LoadingSpinner';
import {
  MacroBriefResponse,
  MacroDerivationMode,
  MacroFrameworkReading,
  MacroFrameworkState,
} from '../../types/macroData';

const fmt = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? '–' : value.toFixed(digits);
const signed = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? '–' : `${value > 0 ? '+' : ''}${value.toFixed(digits)}`;

const INPUT_NAMES: Record<string, string> = {
  m2: 'M2',
  real_gdp: 'Real GDP',
  pce_price_index: 'PCE prices',
  core_pce_price_index: 'Core PCE prices',
  avg_hourly_earnings: 'Avg hourly earnings (real)',
  productivity: 'Productivity',
  fed_funds: 'Fed funds',
  treasury_2y: '2-year Treasury',
  treasury_10y: '10-year Treasury',
  unemployment_rate: 'Unemployment rate',
};

const STATUS_TEXT: Record<string, string> = {
  consistent: 'Money, output and prices roughly consistent',
  money_ahead_of_prices: 'Money growing faster than output plus prices',
  prices_ahead_of_money: 'Prices rising faster than money growth explains',
  in_line: 'Real wages in line with productivity',
  wages_lagging_productivity: 'Real wages lagging productivity',
  wages_outpacing_productivity: 'Real wages outpacing productivity',
  negative: 'Negative real policy rate',
  moderate: 'Moderate real policy rate',
  high: 'High real policy rate',
  tight: 'Tight labour market',
  normal: 'Normal labour slack',
  slack: 'Slack labour market',
  inverted: 'Inverted yield curve',
  flat: 'Flat yield curve',
  upward: 'Upward-sloping yield curve',
  unknown: 'Unknown (an input is missing or stale)',
};

const STATUS_STYLE = (status: string) =>
  status === 'unknown'
    ? 'bg-gray-100 text-gray-600'
    : ['money_ahead_of_prices', 'prices_ahead_of_money', 'wages_lagging_productivity', 'negative', 'high', 'tight', 'slack', 'inverted'].includes(status)
      ? 'bg-amber-100 text-amber-800'
      : 'bg-green-100 text-green-800';

const inputsLine = (r: MacroFrameworkReading<string>) =>
  r.inputs
    .map((i) => `${INPUT_NAMES[i.seriesKey] ?? i.seriesKey} ${i.periodLabel}${i.stale ? ' (stale)' : ''}`)
    .join(' · ');

const ReadingRow: React.FC<{
  title: string;
  idea: string;
  reading: MacroFrameworkReading<string>;
  detail: React.ReactNode;
}> = ({ title, idea, reading, detail }) => (
  <div className="border-b border-gray-100 py-3 last:border-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-sm font-medium text-gray-900">{title}</div>
      <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE(reading.status)}`}>
        {STATUS_TEXT[reading.status] ?? reading.status}
      </span>
    </div>
    <div className="mt-1 text-xs text-gray-500">{idea}</div>
    {reading.status !== 'unknown' && <div className="mt-1 text-sm text-gray-700">{detail}</div>}
    {!!reading.inputs.length && <div className="mt-1 text-[11px] text-gray-400">{inputsLine(reading)}</div>}
  </div>
);

export const ClassicalReadingsCard: React.FC<{ frameworks?: MacroFrameworkState }> = ({ frameworks }) => {
  if (!frameworks) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Classical readings</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">No classical readings stored for this month yet.</p>
        </CardContent>
      </Card>
    );
  }
  const { quantityTheory: qt, realWages: rw, realRates: rr, labor } = frameworks.classical;
  const curve = frameworks.curve;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Classical readings</CardTitle>
          <span className="text-xs text-gray-500">Deterministic; bands are unvalidated defaults</span>
        </div>
      </CardHeader>
      <CardContent>
        <ReadingRow
          title="Quantity of money (MV = PY)"
          idea="If money grows faster than real output, the classical view expects prices to absorb the gap unless velocity falls."
          reading={qt}
          detail={
            <>
              Money {signed(qt.parts.moneyGrowth)}% − real output {signed(qt.parts.realGrowth)}% = excess money{' '}
              {signed(qt.parts.excessMoney)} pp; inflation {signed(qt.parts.inflation)}%. Implied velocity change{' '}
              {signed(qt.value)} pp.
            </>
          }
        />
        <ReadingRow
          title="Real wages and productivity"
          idea="Smith and Ricardo: over time real wages should track labour productivity; a persistent gap shifts income between labour and capital."
          reading={rw}
          detail={
            <>
              Real wage growth {signed(rw.parts.realWageGrowth)}% vs productivity {signed(rw.parts.productivityGrowth)}%:
              gap {signed(rw.value)} pp.
            </>
          }
        />
        <ReadingRow
          title="Real interest rate"
          idea="The price of credit after inflation. Negative real rates reward borrowing over saving; high real rates do the reverse."
          reading={rr}
          detail={
            <>
              Fed funds {fmt(rr.parts.fedFunds, 2)}% − core PCE inflation {fmt(rr.parts.coreInflation)}% ={' '}
              {signed(rr.value)}%
              {rr.parts.real10y !== null && <> · real 10-year {signed(rr.parts.real10y)}%</>}.
            </>
          }
        />
        <ReadingRow
          title="Labour market"
          idea="Unemployment against its trailing 10-year range; Say's law expects slack to clear as wages and prices adjust."
          reading={labor}
          detail={
            <>
              Unemployment {fmt(labor.parts.unemploymentRate)}% (z {signed(labor.value, 2)}); 3-month change{' '}
              {signed(labor.parts.change3m, 2)} pp, {labor.trend}.
            </>
          }
        />
        <ReadingRow
          title="Yield curve"
          idea="Long rates below short rates mean markets price lower future rates; historically this has preceded slowdowns."
          reading={curve}
          detail={
            <>
              10-year {fmt(curve.parts.long, 2)}% − {curve.basis === '10y_fed_funds' ? 'Fed funds' : '2-year'}{' '}
              {fmt(curve.parts.short, 2)}% = {signed(curve.value, 2)} pp.
            </>
          }
        />
      </CardContent>
    </Card>
  );
};

type Tab = 'overview' | 'dalio' | 'classical' | 'weights';

const Section: React.FC<{ title: string; text?: string }> = ({ title, text }) =>
  text ? (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
      <p className="mt-1 whitespace-pre-line text-sm text-gray-800">{text}</p>
    </div>
  ) : null;

export const InterpretationPanel: React.FC<{ mode: MacroDerivationMode; asOfLabel: string }> = ({
  mode,
  asOfLabel,
}) => {
  const [data, setData] = useState<MacroBriefResponse | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      setData(await apiService.getMacroBrief({ mode, asOf: asOfLabel }));
    } catch (err: any) {
      setMessage(err?.response?.data?.message ?? err?.message ?? 'Failed to load interpretation');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [mode, asOfLabel]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setGenerating(true);
    setMessage(null);
    try {
      const outcome = await apiService.generateMacroBrief({ mode, asOf: asOfLabel });
      if (outcome.status === 'busy' || outcome.status === 'no_state') setMessage(outcome.reason);
      else if (outcome.status === 'failed') setMessage(`Generation failed: ${outcome.error}`);
      else if (outcome.status === 'rejected')
        setMessage('The model response failed validation on every attempt, so it was not shown. Try again later.');
      await load();
    } catch (err: any) {
      setMessage(err?.response?.data?.message ?? err?.message ?? 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const record = data?.brief;
  const brief = record?.status === 'accepted' ? record.brief : null;
  const historical = data?.kind === 'historical';
  const shownTab = tab === 'weights' && !brief?.allocation ? 'overview' : tab;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" /> Interpretation · end of {asOfLabel}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {historical && (
              <span className="inline-flex items-center gap-1 rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">
                <GraduationCap className="h-3 w-3" /> Historical replay · teaching material
              </span>
            )}
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">AI-written · unvalidated</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {brief ? (
              <>
                <div>
                  <div className="text-base font-semibold text-gray-900">{brief.headline}</div>
                  {!data?.current && (
                    <div className="mt-1 text-xs text-amber-700">
                      Written before the latest data for this month; regenerate to refresh it.
                    </div>
                  )}
                </div>
                <div className="flex gap-1 border-b border-gray-200">
                  {(
                    [
                      ['overview', 'Overview'],
                      ['dalio', 'Dalio quadrants'],
                      ['classical', 'Classical school'],
                      ...(brief.allocation ? [['weights', 'Target weights']] : []),
                    ] as Array<[Tab, string]>
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
                        shownTab === key ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {shownTab === 'overview' && (
                  <div className="space-y-3">
                    <Section title="Summary" text={brief.summary} />
                    <Section title="Where the lenses agree and differ" text={brief.agreement} />
                    <Section title="Caveats" text={brief.caveats} />
                  </div>
                )}
                {shownTab === 'dalio' && (
                  <div className="space-y-3">
                    <Section title="Reading" text={brief.dalio.reading} />
                    <Section title="What would change it" text={brief.dalio.whatWouldChange} />
                  </div>
                )}
                {shownTab === 'classical' && (
                  <div className="space-y-3">
                    <Section title="Reading" text={brief.classical.reading} />
                    <Section title="Tensions" text={brief.classical.tensions} />
                    <Section title="What would change it" text={brief.classical.whatWouldChange} />
                  </div>
                )}
                {shownTab === 'weights' && brief.allocation && (
                  <div className="space-y-3">
                    <Section title="Why the rules chose these weights" text={brief.allocation.explanation} />
                    <p className="text-xs text-gray-500">
                      The weights are computed by fixed rules, not by the model; the model only explains them.
                    </p>
                  </div>
                )}
                <p className="text-[11px] text-gray-400">
                  Written by {record?.model ?? 'an AI model'} from the computed readings on this page only (no web
                  search). Numbers are checked against those readings; forecasts and investment advice are rejected.
                  {historical && ' Replays are written as of that month-end without later events.'}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">
                {record?.status === 'rejected'
                  ? 'The last attempt failed validation and was not shown.'
                  : historical
                    ? 'No interpretation has been written for this month yet.'
                    : 'No interpretation yet. One is written automatically after each evening ingestion.'}
              </p>
            )}
            {message && <div className="text-sm text-red-700">{message}</div>}
            {(!brief || !data?.current) && (
              <button
                onClick={generate}
                disabled={generating}
                className="inline-flex items-center gap-2 rounded-md border border-purple-300 bg-purple-50 px-3 py-1.5 text-sm text-purple-800 hover:bg-purple-100 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Writing (up to a minute)…' : brief ? 'Regenerate interpretation' : 'Generate interpretation'}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
